/**
 * Editing a clip's framing from the program monitor (1.7.0 stream B, docs/dev/formats-1.7.0-build.md "Editing from the
 * monitor"): which clip a gesture applies to, and the op plus undo coalescing key that sets its pan and zoom in a format.
 * With keys, the key at the playhead (within half a frame) is changed, or one is added there; without keys, the static
 * framing. Pure, so it is unit tested (apps/web/test/monitor-170.test.ts).
 */
import {
  PRIMARY_FORMAT, formatFitOf, framingFor, keyCoalesce, keysOf, layersAt, ops, resolveAt, ticksPerFrame,
  type FormatRef, type FramingKey, type KeyTarget, type LayerClip, type SequenceDocument, type VideoClip,
} from '@prism/contracts';

/** The clip a monitor gesture applies to, at a sequence position. */
export interface EditTarget { clipId: string; versionId: string; layer: boolean; sourceTicks: number }

/**
 * The selected clip when it is on screen at `ph` (a V1 or layer clip), else the V1 clip at `ph` (the incoming one in a
 * transition); null when there is none. `doc` is the document of the format shown (documentForFormat), so a layer hidden
 * in it is not a target.
 */
export function editTargetAt(doc: SequenceDocument, ph: number, selectedClipId: string | null | undefined): EditTarget | null {
  const hit = resolveAt(doc, ph);
  if (selectedClipId) {
    if (hit && hit.placed.clip.id === selectedClipId) return { clipId: selectedClipId, versionId: hit.placed.clip.versionId, layer: false, sourceTicks: hit.sourceTicks };
    if (hit?.outgoing && hit.outgoing.placed.clip.id === selectedClipId) return { clipId: selectedClipId, versionId: hit.outgoing.placed.clip.versionId, layer: false, sourceTicks: hit.outgoing.sourceTicks };
    const l = layersAt(doc, ph).find((x) => x.clip.id === selectedClipId);
    if (l) return { clipId: selectedClipId, versionId: l.clip.versionId, layer: true, sourceTicks: l.sourceTicks };
  }
  return hit ? { clipId: hit.placed.clip.id, versionId: hit.placed.clip.versionId, layer: false, sourceTicks: hit.sourceTicks } : null;
}

const findClip = (doc: SequenceDocument, id: string): VideoClip | LayerClip | null =>
  doc.video.find((c) => c.id === id) ?? (doc.layers ?? []).flatMap((l) => l.clips).find((c) => c.id === id) ?? null;

/** The index of the key within half a frame of `sourceTicks`, or -1. */
export function keyIndexAt(keys: ReadonlyArray<{ atTicks: number }>, sourceTicks: number, tpf: number): number {
  let best = -1, bestD = Infinity;
  keys.forEach((k, i) => { const d = Math.abs(k.atTicks - sourceTicks); if (d <= tpf / 2 && d < bestD) { best = i; bestD = d; } });
  return best;
}

export interface FramingEdit {
  /** The edit for `apply`. */
  fn: (d: SequenceDocument) => SequenceDocument;
  /** Its undo coalescing key (keyCoalesce). */
  coalesce: string;
}

/**
 * The edit that sets a clip's pan (−1…1) and zoom in a format at a source time. `doc` is the editor's document (with
 * formats); the clip's framing in the format decides: with keys, the key at the time is updated or a key added (Smooth);
 * without, the static framing is set (setFormatFraming, which also gives a format its own framing). Null for an unknown clip.
 */
export function framingEdit(doc: SequenceDocument, clipId: string, formatId: FormatRef, sourceTicks: number, next: { x: number; y: number; zoom: number }): FramingEdit | null {
  const clip = findClip(doc, clipId);
  if (!clip) return null;
  const fid = formatId === PRIMARY_FORMAT || (doc.formats ?? []).some((f) => f.id === formatId) ? formatId : PRIMARY_FORMAT;
  const t: KeyTarget = { clipId, formatId: fid, kind: 'framing' };
  const framing = framingFor(clip, fid);
  const value = { x: next.x, y: next.y, zoom: next.zoom };
  if (framing.keys?.length) {
    const tpf = ticksPerFrame(doc.frameRate);
    const i = keyIndexAt(framing.keys, sourceTicks, tpf);
    if (i >= 0) return { fn: (d) => ops.updateKey(d, t, i, value), coalesce: keyCoalesce(t, i) };
    // A new key: its index once added (the ops keep keys in time order), so the rest of the drag edits it as one step.
    const at = Math.max(0, sourceTicks);
    const index = framing.keys.filter((k) => k.atTicks < at - tpf / 2).length;
    return {
      fn: (d) => {
        // A drag that began with no key here may find the key the first move added: update it.
        const now = keysOf(d, t) as FramingKey[];
        const j = keyIndexAt(now, at, tpf);
        return j >= 0 ? ops.updateKey(d, t, j, value) : ops.addKey(d, t, { atTicks: at, ...value, ease: 'smooth' });
      },
      coalesce: keyCoalesce(t, index),
    };
  }
  // A format framed as a whole (1.7.1) keeps the clip's own fit: only the pan and zoom are the clip's.
  const whole = doc.video.some((c) => c.id === clipId) && formatFitOf(doc, fid) !== undefined;
  return { fn: (d) => ops.setFormatFraming(d, clipId, fid, { ...(whole ? {} : { fit: 'fill' as const }), reframeX: value.x, reframeY: value.y, zoom: value.zoom }), coalesce: keyCoalesce(t, 'static') };
}
